import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "./common/DataTable";
import toast from "../utils/notify";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  BugAntIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ClockIcon,
  BellAlertIcon,
  FireIcon,
  CloudIcon,
  LockClosedIcon,
  CpuChipIcon,
  GlobeAltIcon,
  MapPinIcon,
  TrophyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  Cog6ToothIcon,
  PlayIcon,
  PauseIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  PlusIcon,
  StarIcon,
  LightBulbIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  Squares2X2Icon,
  XMarkIcon,
  PencilIcon,
  BookmarkIcon,
  DocumentArrowDownIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";

// Chart.js for enhanced charts
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";

// Recharts for additional visualizations
import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  Area,
  BarChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { useSelector } from "react-redux";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
);

const CentralDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    const savedSettings = localStorage.getItem("platformSettings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      return settings.appearance?.theme === "dark";
    }
    return document.documentElement.classList.contains("dark");
  });
  const [refreshInterval, setRefreshInterval] = useState(60000); // 1 minute
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);

  // Enhanced state management combining all dashboard types
  const [dashboardData, setDashboardData] = useState({
    incidents: [],
    cloudRisks: [],
    dataExposures: [],
    vulnerabilities: [],
    vendorRisks: [],
    trainingProgress: [],
    customers: [],
    complianceStatus: [],
    auditLogs: [],
    analytics: null,
  });

  // Widget configuration
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});

  // Interactive States
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showDetails, setShowDetails] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const isCustomerAdmin =
    user?.role === "customer_admin" || user?.role === "company_user";
  const isPlatformAdmin =
    user?.role === "platform_admin" || user?.role === "admin";
  const dashboardLayoutKey = `central_dashboard_${user?.id}_${user?.role || "unknown"}`;
  const assetCustomerId = useMemo(() => {
    if (user?.role === "customer_admin") return user?.id || user?._id || null;
    if (user?.role === "security_analyst") {
      const analystCustomerId =
        selectedCustomer && selectedCustomer !== "all"
          ? selectedCustomer
          : user?.id;
      return analystCustomerId || null;
    }
    return null;
  }, [user, selectedCustomer]);

  // Enhanced Widget Library combining all dashboard types
  const widgetLibrary = [
    {
      id: "ai_insights_panel",
      name: "AI Insights Panel",
      category: "AI",
      icon: LightBulbIcon,
      type: "ai_insights",
      size: { width: 4, height: 1 },
      description: "Top 5 AI-powered emerging risks with confidence scores",
    },
    {
      id: "incident_heatmap",
      name: "Incident Heatmap",
      category: "Security",
      icon: ExclamationTriangleIcon,
      type: "heatmap",
      size: { width: 2, height: 1 },
      description: "Interactive security incident grid visualization",
    },
    {
      id: "cloud_security_gauge",
      name: "Cloud Security Posture",
      category: "Cloud",
      icon: CloudIcon,
      type: "gauge",
      size: { width: 1, height: 1 },
      description: "Real-time cloud security posture score",
    },
    {
      id: "vulnerability_snapshot",
      name: "Vulnerability Snapshot",
      category: "Security",
      icon: BugAntIcon,
      type: "severity_breakdown",
      size: { width: 1, height: 1 },
      description: "Critical, High, Medium, Low vulnerability counts",
    },
    {
      id: "compliance_score_card",
      name: "Compliance Score",
      category: "Compliance",
      icon: ShieldCheckIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      description: "Overall compliance percentage with trend",
    },
    {
      id: "risk_overview_card",
      name: "Risk Overview",
      category: "Risk",
      icon: ExclamationTriangleIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      description: "Total risks with severity breakdown",
    },
    {
      id: "training_leaderboard",
      name: "Training Leaderboard",
      category: "Learning",
      icon: TrophyIcon,
      type: "leaderboard",
      size: { width: 1, height: 2 },
      description: "Top performers with gamification elements",
    },
    {
      id: "data_exposure_map",
      name: "Data Exposure Map",
      category: "Data",
      icon: MapPinIcon,
      type: "geo_map",
      size: { width: 1, height: 1 },
      description: "Geographic data exposure distribution",
    },
    {
      id: "vendor_risk_radar",
      name: "Vendor Risk Radar",
      category: "TPRM",
      icon: BuildingOfficeIcon,
      type: "radar_chart",
      size: { width: 1, height: 1 },
      description: "Third-party vendor risk assessment",
    },
    {
      id: "customer_management",
      name: "Customer Management",
      category: "Business",
      icon: UserGroupIcon,
      type: "data_table",
      size: { width: 2, height: 1 },
      description: "Customer inventory and module management",
    },
    {
      id: "system_health_gauge",
      name: "System Health",
      category: "Operations",
      icon: FireIcon,
      type: "circular_gauge",
      size: { width: 1, height: 1 },
      description: "System uptime and performance metrics",
    },
    {
      id: "compliance_frameworks",
      name: "Compliance Frameworks",
      category: "Compliance",
      icon: DocumentTextIcon,
      type: "framework_status",
      size: { width: 2, height: 1 },
      description: "Framework compliance status and progress",
    },
  ];

  useEffect(() => {
    initializeDashboard();

    // Listen for theme changes
    const handleThemeChange = (event) => {
      setDarkMode(event.detail.theme === "dark");
    };

    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadDashboardData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [selectedCustomer, autoRefresh, refreshInterval]);

  const initializeDashboard = async () => {
    setLoading(true);

    // Load saved layout or create default based on user role
    if (isCustomerAdmin) {
      setSelectedCustomer(user?.id || user?._id || "all");
    }

    const savedLayout = localStorage.getItem(dashboardLayoutKey);
    if (savedLayout) {
      const layout = JSON.parse(savedLayout);
      setSelectedWidgets(layout.widgets || []);
    } else {
      const defaultWidgets = getDefaultWidgetsForRole(user?.role);
      setSelectedWidgets(defaultWidgets);
    }

    await loadDashboardData();
    setLoading(false);
  };

  const getDefaultWidgetsForRole = (role) => {
    const createWidget = (widgetTemplate, position) => ({
      ...widgetTemplate,
      id: `${widgetTemplate.id}_${Date.now()}_${Math.random()}`,
      position,
    });

    const adminWidgets = [
      createWidget(widgetLibrary[0], { x: 0, y: 0 }), // AI Insights Panel (full width)
      createWidget(widgetLibrary[1], { x: 0, y: 1 }), // Incident Heatmap
      createWidget(widgetLibrary[2], { x: 2, y: 1 }), // Cloud Security Gauge
      createWidget(widgetLibrary[3], { x: 3, y: 1 }), // Vulnerability Snapshot
      createWidget(widgetLibrary[4], { x: 0, y: 2 }), // Compliance Score
      createWidget(widgetLibrary[5], { x: 1, y: 2 }), // Risk Overview
      createWidget(widgetLibrary[6], { x: 2, y: 2 }), // Training Leaderboard
      createWidget(widgetLibrary[7], { x: 3, y: 2 }), // Data Exposure Map
      createWidget(widgetLibrary[9], { x: 0, y: 3 }), // Customer Management
    ];

    const userWidgets = [
      createWidget(widgetLibrary[0], { x: 0, y: 0 }), // AI Insights Panel
      createWidget(widgetLibrary[4], { x: 0, y: 1 }), // Compliance Score
      createWidget(widgetLibrary[5], { x: 1, y: 1 }), // Risk Overview
      createWidget(widgetLibrary[2], { x: 2, y: 1 }), // Cloud Security Gauge
      createWidget(widgetLibrary[6], { x: 0, y: 2 }), // Training Leaderboard
    ];

    return isPlatformAdmin ? adminWidgets : userWidgets;
  };

  // ========================================
  // UNIFIED DATA LOADING
  // ========================================

  const endUserDashboard = user?.employee_details?.dashboard;

  const renderEndUserSummary = () => {
    if (!endUserDashboard) return null;

    return (
      <div className="mb-6 px-4 w-full">
        <div className="rounded-3xl border p-4 bg-white shadow-sm dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                My End User Dashboard
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your latest assigned risks, controls, and LMS items.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border p-4 bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Assigned Risks
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {endUserDashboard.risks?.count ?? 0}
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Recent: {endUserDashboard.risks?.items?.length ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border p-4 bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Assigned Controls
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {endUserDashboard.controls?.count ?? 0}
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Pending responses:{" "}
                {endUserDashboard.controls?.items?.filter(
                  (c) => c.compliance_status !== "completed",
                ).length ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border p-4 bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Assigned LMS Modules
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {endUserDashboard.lms?.count ?? 0}
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Completed:{" "}
                {endUserDashboard.lms?.items?.filter((m) => m.is_completed)
                  .length ?? 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      let assetAgentData = null;
      if (assetCustomerId) {
        try {
          const assetResponse = await fetch(
            `${BACKEND_URL}/api/asset-connectors/connectors/agent_dashboard/?customer_id=${assetCustomerId}`,
            { headers: getAuthHeaders() },
          );
          if (assetResponse.ok) {
            assetAgentData = await assetResponse.json();
          }
        } catch (error) {
          console.warn(
            "Asset manager agent data unavailable for central dashboard:",
            error,
          );
        }
      }

      const customerQuery =
        selectedCustomer !== "all"
          ? `?customer_id=${encodeURIComponent(selectedCustomer)}`
          : "";
      const riskCustomerId =
        selectedCustomer !== "all" ? selectedCustomer : user?.id || user?._id;
      const moduleEndpoints = {
        virtualSOC: "/api/soc/dashboard/",
        cspm: "/api/ai-cspm/dashboard/stats/",
        dspm: "/api/ai_dspm/dashboard/stats/",
        vapt: `/api/vapt/dashboard/${customerQuery}`,
        tprm: "/api/dashboard/live/tprm/",
        lms: "/api/dashboard/live/lms/",
        customers: "/api/ai-crm/api/v1/dashboard/stats/",
        risk: riskCustomerId
          ? `/api/risks-management/dashboard/?userId=${encodeURIComponent(riskCustomerId)}`
          : null,
        // Company overview is per company; a platform admin has none (404).
        analytics: isPlatformAdmin ? null : "/api/dashboard/company-overview",
      };
      const requestedModules = Object.entries(moduleEndpoints).filter(
        ([, endpoint]) => endpoint,
      );
      const responses = await Promise.allSettled(
        requestedModules.map(([, endpoint]) =>
          fetch(`${BACKEND_URL}${endpoint}`, { headers: getAuthHeaders() }),
        ),
      );
      const moduleData = Object.fromEntries(
        await Promise.all(
          requestedModules.map(async ([module, _endpoint], index) => {
            const response = responses[index];
            if (response.status !== "fulfilled" || !response.value.ok) {
              return [module, {}];
            }
            try {
              return [module, await response.value.json()];
            } catch {
              return [module, {}];
            }
          }),
        ),
      );

      // Process unified data
      processUnifiedData(moduleData, assetAgentData);

      // Generate AI insights
      generateAIInsights(moduleData, assetAgentData);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      toast.error("Error loading dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const processUnifiedData = (moduleData, assetAgentData = null) => {
    const unwrap = (payload) => payload?.data || payload || {};
    const soc = unwrap(moduleData.virtualSOC);
    const cspm = unwrap(moduleData.cspm);
    const dspm = unwrap(moduleData.dspm);
    const vapt = unwrap(moduleData.vapt);
    const tprm = unwrap(moduleData.tprm);
    const lms = unwrap(moduleData.lms);
    const crm = unwrap(moduleData.customers);
    const overview = unwrap(moduleData.analytics);
    const risk = unwrap(moduleData.risk);
    const assetSummary =
      assetAgentData?.stats || assetAgentData?.agent?.summary || {};
    const assetInventory = assetSummary.asset_inventory || [];
    const cloudAssets = assetInventory.filter(
      (asset) =>
        !["github", "gitlab"].includes(
          String(asset.provider || "").toLowerCase(),
        ),
    );
    const cspmFindings =
      cspm.agent?.summary?.findings ||
      cspm.findings ||
      cspm.open_findings_list ||
      [];
    const vulnerabilityCounts = vapt.findings_by_severity || {};
    const realIncidents = (soc.recent_alerts || []).map((alert, index) => ({
      id: alert.id || alert.alert_id || `alert-${index}`,
      title:
        alert.name || alert.description || alert.alert_id || "Security alert",
      severity: String(alert.severity || "low").toLowerCase(),
      status: String(alert.status || "open").toLowerCase(),
      assignedTo: alert.analyst || "Unassigned",
      mitreMapping: alert.mitre_technique || "",
      x: index % 10,
      y: Math.floor(index / 10),
    }));
    const exposureCounts = cloudAssets
      .filter((asset) => asset.public_access || asset.contains_sensitive_data)
      .reduce((counts, asset) => {
        const region = asset.region || "Unknown";
        counts[region] = (counts[region] || 0) + 1;
        return counts;
      }, {});
    const vendorInventory =
      tprm.agent?.summary?.vendor_inventory || tprm.vendors || [];
    const trainingRows =
      lms.agent?.summary?.courses || lms.courses || lms.leaderboard || [];
    const recentCustomers = crm.recent_customers || [];

    setDashboardData({
      incidents: realIncidents,
      cloudRisks: cspmFindings.map((finding, index) => ({
        id: finding.finding_id || finding.id || `finding-${index}`,
        resource:
          finding.title ||
          finding.resource_name ||
          finding.finding_id ||
          "Cloud finding",
        provider: finding.cloud_provider || "Unknown",
        service: finding.resource_type || finding.service || "Unknown",
        risk: finding.severity || "low",
        severity: String(finding.severity || "low").toLowerCase(),
        exposure: finding.category || "Cloud finding",
        region: finding.region || "Unknown",
        status: finding.status || "open",
      })),
      dataExposures: Object.entries(exposureCounts).map(
        ([name, exposures]) => ({
          name,
          exposures,
        }),
      ),
      vulnerabilities: Object.fromEntries(
        ["critical", "high", "medium", "low"].map((severity) => {
          const match = Object.entries(vulnerabilityCounts).find(
            ([key]) => key.toLowerCase() === severity,
          );
          return [severity, Number(match?.[1] || 0)];
        }),
      ),
      vendorRisks: Array.isArray(vendorInventory) ? vendorInventory : [],
      trainingProgress: Array.isArray(trainingRows) ? trainingRows : [],
      customers: recentCustomers,
      complianceStatus:
        overview.frameworks?.list || cspm.compliance_scores || [],
      auditLogs: overview.audit_logs || [],
      analytics: {
        ...overview,
        cspm,
        dspm,
        vapt,
        risk,
        asset_agent: assetSummary,
        asset_summary: {
          total_assets: Number(assetSummary.total_assets || cloudAssets.length),
          public_assets: Number(
            assetSummary.public_assets ||
              cloudAssets.filter((asset) => asset.public_access).length,
          ),
          sensitive_data_assets: Number(
            assetSummary.sensitive_data_assets_count ||
              cloudAssets.filter((asset) => asset.contains_sensitive_data)
                .length,
          ),
          risk_score: Number(assetSummary.risk_score || 0),
          risk_level: assetSummary.risk_level || "low",
        },
        compliance: {
          compliance_rate:
            overview.compliance_rate ?? cspm.security_score ?? null,
        },
        system_health_score:
          soc.monitored_assets > 0
            ? Math.round(
                ((soc.monitored_assets - (soc.high_risk_assets || 0)) /
                  soc.monitored_assets) *
                  100,
              )
            : null,
      },
    });
  };

  const generateAIInsights = (moduleData, assetAgentData = null) => {
    const sources = [
      ["Virtual SOC", moduleData.virtualSOC],
      ["AI CSPM", moduleData.cspm],
      ["DSPM", moduleData.dspm],
      ["VAPT", moduleData.vapt],
      ["TPRM", moduleData.tprm],
      ["LMS", moduleData.lms],
      ["Asset Management", assetAgentData],
    ];
    const insights = sources.flatMap(([module, response]) => {
      const payload = response?.data || response || {};
      const agent = payload.agent || response?.agent;
      return (agent?.alerts || []).map((alert, index) => ({
        id: `${module}-${alert.id || alert.title || index}`,
        title: alert.title || alert.name || "Agent alert",
        description: alert.message || alert.description || "",
        severity: String(alert.severity || "low").toLowerCase(),
        module,
        confidence: alert.confidence,
        action: alert.recommended_action || alert.action || "",
      }));
    });
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    insights.sort(
      (left, right) =>
        (severityOrder[left.severity] ?? 4) -
        (severityOrder[right.severity] ?? 4),
    );

    setWidgetData((previous) => ({
      ...previous,
      ai_insights: insights.slice(0, 5),
    }));
  };

  // Navigation handler for widgets
  const handleWidgetNavigation = (module) => {
    const routes = {
      "virtual-soc": "/virtual-soc",
      "ai-cspm": "/ai-cspm",
      dspm: "/dspm",
      vapt: "/vapt",
      tprm: "/tprm",
      lms: "/lms",
      crm: "/ai-crm",
      incidents: "/incident-reporting",
      compliance: "/compliance",
      risk: "/risk-management",
      customers: "/customer-inventory",
    };

    if (routes[module]) {
      navigate(routes[module]);
    }
  };

  // Widget rendering system
  const renderWidget = (widget) => {
    const data = widgetData[widget.id] || dashboardData;

    switch (widget.type) {
      case "ai_insights":
        return (
          <AIInsightsPanel
            widget={widget}
            data={widgetData.ai_insights}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "heatmap":
        return (
          <IncidentHeatmapWidget
            widget={widget}
            data={dashboardData.incidents}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "gauge":
        return (
          <CloudSecurityGauge
            widget={widget}
            data={data}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "severity_breakdown":
        return (
          <VulnerabilitySnapshot
            widget={widget}
            data={dashboardData.vulnerabilities}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "kpi_card":
        return (
          <KPICard
            widget={widget}
            data={data}
            analytics={dashboardData.analytics}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "leaderboard":
        return (
          <TrainingLeaderboard
            widget={widget}
            data={dashboardData.trainingProgress}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "geo_map":
        return (
          <DataExposureMap
            widget={widget}
            data={dashboardData.dataExposures}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "radar_chart":
        return (
          <VendorRiskRadar
            widget={widget}
            data={dashboardData.vendorRisks}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "data_table":
        return (
          <CustomerManagementWidget
            widget={widget}
            data={dashboardData.customers}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "circular_gauge":
        return (
          <SystemHealthGauge
            widget={widget}
            data={data}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      case "framework_status":
        return (
          <ComplianceFrameworks
            widget={widget}
            data={dashboardData.analytics}
            darkMode={darkMode}
            onNavigate={handleWidgetNavigation}
          />
        );
      default:
        return (
          <div className="p-4 text-center text-gray-500">
            Widget type not supported
          </div>
        );
    }
  };

  // ========================================
  // INTERACTIVE HANDLERS
  // ========================================

  const handleWidgetClick = (widgetType, item) => {
    setSelectedWidget(widgetType);

    switch (widgetType) {
      case "incident":
        openIncidentPlaybook(item);
        break;
      case "cloud_risk":
        openMisconfigDetail(item);
        break;
      case "vulnerability":
        assignToRemediationOwner(item);
        break;
      case "vendor":
        openVendorProfile(item);
        break;
      case "training":
        awardBadge(item);
        break;
      case "customer":
        modifyCustomerModules(item);
        break;
      default:
        break;
    }
  };

  const handleItemHover = (item, show = true) => {
    if (show) {
      setHoveredItem(item);
    } else {
      setHoveredItem(null);
    }
  };

  // Cross-module actions
  const openIncidentPlaybook = (incident) => {
    toast.success(`Opening playbook for ${incident.title}`);
  };

  const openMisconfigDetail = (misconfig) => {
    toast.info(`Opening details for ${misconfig.resource || "cloud resource"}`);
  };

  const assignToRemediationOwner = (vulnerability) => {
    toast.success(
      `Assigned ${vulnerability.title || "vulnerability"} to remediation team`,
    );
  };

  const openVendorProfile = (vendor) => {
    toast.info(`Opening profile for ${vendor.name}`);
  };

  const awardBadge = (user) => {
    toast.success(`🏆 Badge awarded to ${user.name}!`);
  };

  const modifyCustomerModules = (customer) => {
    toast.info(`Managing modules for ${customer.name || "customer"}`);
  };

  const switchCustomer = (customerId) => {
    setSelectedCustomer(customerId);
    toast.info(
      customerId === "all"
        ? "Viewing all customers"
        : `Switched to customer: ${customerId}`,
    );
  };

  const exportComplianceReport = () => {
    toast.success("Generating compliance report for audit...");
  };

  const addWidgetToDashboard = (widget) => {
    const newWidget = {
      ...widget,
      id: `${widget.id}_${Date.now()}`,
      position: findEmptyPosition(),
    };

    setSelectedWidgets((prev) => [...prev, newWidget]);
    setShowWidgetLibrary(false);
    saveDashboard([...selectedWidgets, newWidget]);
  };

  const removeWidget = (widgetId) => {
    const updatedWidgets = selectedWidgets.filter((w) => w.id !== widgetId);
    setSelectedWidgets(updatedWidgets);
    saveDashboard(updatedWidgets);
  };

  const findEmptyPosition = () => {
    const occupied = selectedWidgets.map((w) => w.position);
    for (let y = 0; y < 10; y++) {
      for (let x = 0; x < 4; x++) {
        if (!occupied.some((pos) => pos.x === x && pos.y === y)) {
          return { x, y };
        }
      }
    }
    return { x: 0, y: selectedWidgets.length };
  };

  const saveDashboard = (widgets = selectedWidgets) => {
    const dashboardData = {
      widgets,
      config: { darkMode, refreshInterval, selectedCustomer },
      lastSaved: new Date().toISOString(),
    };

    localStorage.setItem(dashboardLayoutKey, JSON.stringify(dashboardData));
    toast.success("Dashboard saved successfully!");
  };

  // Utility functions
  const getSeverityColor = (severity) => {
    const colors = {
      critical: "bg-red-100 text-red-800 border-red-200",
      high: "bg-orange-100 text-orange-800 border-orange-200",
      medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
      low: "bg-green-100 text-green-800 border-green-200",
    };
    return colors[severity] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "bg-blue-100 text-blue-800 border-blue-200",
      in_progress: "bg-yellow-100 text-yellow-800 border-yellow-200",
      contained: "bg-purple-100 text-purple-800 border-purple-200",
      resolved: "bg-green-100 text-green-800 border-green-200",
      closed: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <div className="w-full">
      {/* Enhanced Header */}
      <div className="mb-6 px-4 pt-4 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <div className="">
              <ChartBarIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Central Command Dashboard
              </h1>
              <p className="text-sm text-gray-600">
                Unified Security & Compliance Control Center • Welcome back,{" "}
                {user?.name?.split(" ")[0] || "User"}!
              </p>
            </div>
          </div>

          {/* Enhanced Controls */}
          <div className="flex items-center space-x-3">
            {/* Customer Selector is platform-wide only. Customer admins stay scoped to their tenant. */}
            {!isCustomerAdmin && (
              <select
                value={selectedCustomer}
                onChange={(e) => switchCustomer(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  darkMode
                    ? "bg-gray-800 border-gray-700 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                } focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Customers</option>
                {dashboardData.customers.map((customer) => {
                  const customerId = customer.user_id || customer.id;
                  return customerId ? (
                    <option key={customerId} value={customerId}>
                      {customer.company_name ||
                        customer.name ||
                        customer.email ||
                        customerId}
                    </option>
                  ) : null;
                })}
              </select>
            )}

            {/* Widget Library */}
            <button
              onClick={() => setShowWidgetLibrary(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Widget
            </button>

            {/* Edit Mode Toggle */}
            <button
              onClick={() => setEditMode(!editMode)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                editMode
                  ? "bg-green-600 text-white"
                  : darkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-200 text-gray-700"
              }`}
            >
              {editMode ? (
                <CheckCircleIcon className="w-5 h-5 mr-2" />
              ) : (
                <PencilIcon className="w-5 h-5 mr-2" />
              )}
              {editMode ? "Done" : "Edit"}
            </button>

            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                autoRefresh
                  ? "bg-green-600 text-white"
                  : darkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-200 text-gray-700"
              }`}
            >
              {autoRefresh ? (
                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <PauseIcon className="w-5 h-5 mr-2" />
              )}
              {autoRefresh ? "Auto Refresh" : "Paused"}
            </button>

            {/* Theme Mode Toggle (light → semi-dark → dark → light) */}
            <button
              onClick={() => {
                const savedSettings = localStorage.getItem("platformSettings");
                let settings = savedSettings
                  ? JSON.parse(savedSettings)
                  : {
                      appearance: {
                        theme: "light",
                        primaryColor: "#3b82f6",
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontSize: 14,
                        fontWeight: "400",
                        lineHeight: 1.5,
                        letterSpacing: 0,
                        borderRadius: 8,
                      },
                    };
                const currentTheme = settings.appearance?.theme || "light";
                const cycle = {
                  light: "semi-dark",
                  "semi-dark": "dark",
                  dark: "light",
                };
                const newTheme = cycle[currentTheme] || "light";
                settings.appearance.theme = newTheme;
                localStorage.setItem(
                  "platformSettings",
                  JSON.stringify(settings),
                );

                document.documentElement.classList.remove("dark", "semi-dark");
                if (newTheme === "dark") {
                  document.documentElement.classList.add("dark");
                } else if (newTheme === "semi-dark") {
                  document.documentElement.classList.add("semi-dark");
                }

                window.dispatchEvent(
                  new CustomEvent("themeChanged", {
                    detail: { theme: newTheme },
                  }),
                );
                setDarkMode(newTheme === "dark");
              }}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 text-yellow-400"
                  : "bg-gray-200 text-gray-700"
              }`}
              title={darkMode ? "Switch to Light" : "Cycle Theme"}
            >
              {darkMode ? "🌙" : "☀️"}
            </button>

            {/* Export Button */}
            <button
              onClick={exportComplianceReport}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* End-user dashboard summary */}
      {user?.role === "end_user" && renderEndUserSummary()}

      {/* Main Dashboard Grid */}
      <div className="px-4 w-full">
        {loading && (
          <div className="flex justify-center items-center py-12">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-600" />
            <span
              className={`ml-2 ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              Loading dashboard...
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
          {selectedWidgets.map((widget) => (
            <motion.div
              key={widget.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={editMode ? { scale: 1.02 } : {}}
              className={`rounded-xl shadow-sm border overflow-hidden ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              } ${widget.size.width === 2 ? "lg:col-span-2" : ""} ${
                widget.size.width === 4 ? "xl:col-span-4" : ""
              } ${widget.size.height === 2 ? "row-span-2" : ""} ${
                editMode ? "ring-2 ring-blue-300" : ""
              }`}
            >
              {editMode && (
                <div className="absolute top-2 right-2 z-10 flex space-x-1">
                  <button
                    onClick={() => removeWidget(widget.id)}
                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="h-full">{renderWidget(widget)}</div>
            </motion.div>
          ))}

          {/* Add Widget Placeholder in Edit Mode */}
          {editMode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`border-2 border-dashed rounded-xl p-8 flex items-center justify-center cursor-pointer transition-colors ${
                darkMode
                  ? "border-gray-600 hover:border-blue-400 hover:bg-gray-700"
                  : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
              }`}
              onClick={() => setShowWidgetLibrary(true)}
            >
              <div className="text-center">
                <PlusIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Add New Widget</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Widget Library Modal */}
      <AnimatePresence>
        {showWidgetLibrary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowWidgetLibrary(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden ${
                darkMode ? "bg-gray-800" : "bg-white"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`p-6 border-b ${
                  darkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h2
                    className={`text-2xl font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Widget Library
                  </h2>
                  <button
                    onClick={() => setShowWidgetLibrary(false)}
                    className={`p-2 rounded-lg ${
                      darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                    }`}
                  >
                    <XMarkIcon
                      className={`w-6 h-6 ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    />
                  </button>
                </div>
                <p
                  className={`mt-2 ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Choose widgets to add to your dashboard
                </p>
              </div>

              <div className="p-6 overflow-y-auto max-h-96">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {widgetLibrary.map((widget) => (
                    <motion.div
                      key={widget.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`p-4 border rounded-lg cursor-pointer hover:shadow-md ${
                        darkMode
                          ? "border-gray-700 hover:border-blue-500 bg-gray-700"
                          : "border-gray-200 hover:border-blue-300 bg-white"
                      }`}
                      onClick={() => addWidgetToDashboard(widget)}
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            darkMode ? "bg-blue-900" : "bg-blue-100"
                          }`}
                        >
                          <widget.icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3
                            className={`font-medium ${
                              darkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {widget.name}
                          </h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              darkMode
                                ? "text-blue-300 bg-blue-900"
                                : "text-blue-600 bg-blue-100"
                            }`}
                          >
                            {widget.category}
                          </span>
                        </div>
                      </div>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {widget.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ========================================
// WIDGET COMPONENTS
// ========================================

const AIInsightsPanel = ({ widget, data, darkMode, onNavigate }) => (
  <div className="p-6">
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold flex items-center ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        <LightBulbIcon className="w-6 h-6 mr-2 text-yellow-500" />
        AI Insights - Current Module Alerts
      </h3>
      <div className="flex items-center space-x-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span
          className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          Live Analysis
        </span>
      </div>
    </div>

    {(data || []).length === 0 && (
      <p
        className={darkMode ? "text-sm text-gray-400" : "text-sm text-gray-500"}
      >
        No active agent alerts from the connected modules.
      </p>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {(data || []).map((insight, index) => (
        <motion.div
          key={insight.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-105 ${
            darkMode
              ? insight.severity === "critical"
                ? "bg-red-900/30 border-red-700 hover:bg-red-900/40"
                : insight.severity === "high"
                  ? "bg-orange-900/30 border-orange-700 hover:bg-orange-900/40"
                  : "bg-yellow-900/30 border-yellow-700 hover:bg-yellow-900/40"
              : insight.severity === "critical"
                ? "bg-red-50 border-red-200 hover:bg-red-100"
                : insight.severity === "high"
                  ? "bg-orange-50 border-orange-200 hover:bg-orange-100"
                  : "bg-yellow-50 border-yellow-200 hover:bg-yellow-100"
          }`}
          onClick={() => {
            const moduleMap = {
              "Virtual SOC": "virtual-soc",
              "AI CSPM": "ai-cspm",
              DSPM: "dspm",
              VAPT: "vapt",
              TPRM: "tprm",
              LMS: "lms",
              "AI CRM": "crm",
            };
            const module = moduleMap[insight.module];
            if (module && onNavigate) {
              onNavigate(module);
            }
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                insight.severity === "critical"
                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                  : insight.severity === "high"
                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              }`}
            >
              {insight.severity.toUpperCase()}
            </span>
            <span
              className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {Number.isFinite(insight.confidence)
                ? `${Math.round(insight.confidence * 100)}%`
                : ""}
            </span>
          </div>
          <h4
            className={`font-medium text-sm mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {insight.title}
          </h4>
          <p
            className={`text-xs mb-2 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {insight.description}
          </p>
          {insight.action && (
            <p
              className={`text-xs font-medium ${darkMode ? "text-blue-400" : "text-blue-600"}`}
            >
              {insight.action}
            </p>
          )}
        </motion.div>
      ))}
    </div>
  </div>
);

const IncidentHeatmapWidget = ({ widget, data, darkMode, onNavigate }) => (
  <div
    className="p-6"
    style={{ cursor: "pointer" }}
    onClick={() => onNavigate && onNavigate("virtual-soc")}
  >
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold flex items-center ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        <ExclamationTriangleIcon className="w-6 h-6 mr-2 text-red-500" />
        Incident Heatmap
      </h3>
      <span
        className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}
      >
        {(data || []).filter((i) => i.status === "open").length} Open
      </span>
    </div>

    <div className="grid grid-cols-10 gap-1">
      {(data || []).map((incident, index) => (
        <div
          key={incident.id}
          className={`w-6 h-6 rounded cursor-pointer transition-all hover:scale-110 ${
            incident.severity === "critical"
              ? "bg-red-500"
              : incident.severity === "high"
                ? "bg-orange-500"
                : incident.severity === "medium"
                  ? "bg-yellow-500"
                  : "bg-green-500"
          }`}
          style={{ opacity: incident.status === "open" ? 1 : 0.5 }}
          title={`${incident.title} - ${incident.severity} - SLA: ${incident.slaTimer}h`}
        />
      ))}
    </div>
  </div>
);

const CloudSecurityGauge = ({ widget, data, darkMode, onNavigate }) => {
  const scoreValue =
    data?.analytics?.cspm?.security_score ??
    data?.analytics?.cspm?.agent?.summary?.average_security_score;
  const score = Number.isFinite(Number(scoreValue)) ? Number(scoreValue) : null;

  return (
    <div
      className="p-6"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("ai-cspm")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <CloudIcon className="w-6 h-6 mr-2 text-blue-500" />
          Cloud Security Posture
        </h3>
        <button className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          Auto-Sync
        </button>
      </div>

      <div className="text-center">
        <div className="relative w-32 h-32 mx-auto mb-4">
          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
            <path
              className={darkMode ? "stroke-gray-600" : "stroke-gray-300"}
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="2"
            />
            <path
              className={`${
                score >= 80
                  ? "stroke-green-500"
                  : score >= 50
                    ? "stroke-yellow-500"
                    : "stroke-red-500"
              }`}
              strokeDasharray={`${score ?? 0}, 100`}
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="2"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {score === null ? "--" : Math.round(score)}
            </span>
          </div>
        </div>
        <div
          className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          {score === null
            ? "No cloud score available"
            : score >= 80
              ? "Excellent"
              : score >= 50
                ? "Good"
                : "Needs Attention"}
        </div>
      </div>
    </div>
  );
};

const VulnerabilitySnapshot = ({ widget, data, darkMode, onNavigate }) => (
  <div
    className="p-6"
    style={{ cursor: "pointer" }}
    onClick={() => onNavigate && onNavigate("vapt")}
  >
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold flex items-center ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        <BugAntIcon className="w-6 h-6 mr-2 text-red-500" />
        Vulnerability Snapshot
      </h3>
    </div>

    <div className="space-y-3">
      {Object.entries(data || {}).map(([severity, count]) => (
        <div
          key={severity}
          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all hover:scale-105 ${
            severity === "critical"
              ? "bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30"
              : severity === "high"
                ? "bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30"
                : severity === "medium"
                  ? "bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30"
                  : "bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30"
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-4 h-4 rounded ${
                severity === "critical"
                  ? "bg-red-500"
                  : severity === "high"
                    ? "bg-orange-500"
                    : severity === "medium"
                      ? "bg-yellow-500"
                      : "bg-green-500"
              }`}
            />
            <span
              className={`font-medium capitalize ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {severity}
            </span>
          </div>
          <span
            className={`font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {count}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const KPICard = ({ widget, data, analytics, darkMode, onNavigate }) => {
  let value, label, trend, module, description, IconComponent;

  if (widget.id.includes("compliance")) {
    value = `${Number(analytics?.compliance?.compliance_rate || 0).toFixed(1)}%`;
    label = "Compliance Rate";
    trend = null;
    module = "compliance";
    description = "Overall compliance percentage with trend";
    IconComponent = ShieldCheckIcon;
  } else if (widget.id.includes("risk")) {
    value = `${Number(analytics?.risk?.risk_score || 0).toFixed(1)}%`;
    label = "Risk Score";
    trend = null;
    module = "risk";
    description = "Total risks with severity breakdown";
    IconComponent = ExclamationTriangleIcon;
  } else {
    // Fallback for any other KPI card
    IconComponent = ShieldCheckIcon;
  }

  return (
    <div
      className="p-4 flex flex-col justify-between h-full min-h-[180px]"
      style={{ cursor: "pointer" }}
      onClick={() => module && onNavigate && onNavigate(module)}
    >
      <div className="flex items-center justify-between mb-2">
        <IconComponent
          className={`w-6 h-6 ${darkMode ? "text-blue-400" : "text-blue-600"}`}
        />
        {trend && (
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${
              trend?.startsWith("+")
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <p
          className={`text-3xl font-bold mb-1 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {value}
        </p>
        <p
          className={`text-sm font-semibold mb-1 ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          {label}
        </p>
        <p
          className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

const TrainingLeaderboard = ({ widget, data, darkMode, onNavigate }) => {
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : TrophyIcon;

  return (
    <div
      className="p-6 h-full"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("lms")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-yellow-500" />
          Training Activity
        </h3>
      </div>

      <div className="space-y-3">
        {(data || []).slice(0, 5).map((user, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all hover:scale-105 ${
              darkMode
                ? "bg-gray-700 hover:bg-gray-600"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
            onClick={() => toast.success(`🏆 Badge awarded to ${user.name}!`)}
          >
            <div className="flex items-center space-x-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index === 0
                    ? "bg-yellow-500 text-white"
                    : index === 1
                      ? "bg-gray-400 text-white"
                      : index === 2
                        ? "bg-orange-600 text-white"
                        : "bg-gray-200 text-gray-700"
                }`}
              >
                {index + 1}
              </div>
              <div>
                <div
                  className={`font-medium ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {user.name}
                </div>
                <div
                  className={`text-sm ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {user.department}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div
                className={`font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {user.progress != null
                  ? `${user.progress}% complete`
                  : `${user.points} pts`}
              </div>
              <div
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                🏆 {user.badges} badges
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DataExposureMap = ({ widget, data, darkMode, onNavigate }) => {
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : MapPinIcon;

  return (
    <div
      className="p-6"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("dspm")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-purple-500" />
          Data Exposure Map
        </h3>
      </div>

      <div className="space-y-3">
        {(data || []).map((location, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-105 ${
              darkMode
                ? "bg-gray-700 hover:bg-gray-600"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
            onClick={() =>
              toast.info(`Viewing ${location.name} data inventory`)
            }
          >
            <div className="flex items-center justify-between">
              <div>
                <div
                  className={`font-medium ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {location.name}
                </div>
                <div
                  className={`text-sm ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {location.exposures} exposures
                </div>
              </div>
              <div
                className={`w-3 h-3 rounded-full ${
                  location.exposures > 30
                    ? "bg-red-500"
                    : location.exposures > 15
                      ? "bg-yellow-500"
                      : "bg-green-500"
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VendorRiskRadar = ({ widget, data, darkMode, onNavigate }) => {
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : BuildingOfficeIcon;

  return (
    <div
      className="p-6"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("tprm")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-green-500" />
          Vendor Risk
        </h3>
      </div>

      <div className="space-y-2">
        {(data || []).slice(0, 3).map((vendor, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-2 rounded ${
              darkMode ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            <span
              className={`text-sm ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {vendor.name}
            </span>
            <span
              className={`text-xs px-2 py-1 rounded ${
                vendor.security > 70
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {vendor.security}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomerManagementWidget = ({ widget, data, darkMode, onNavigate }) => {
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : UserGroupIcon;

  return (
    <div
      className="p-6"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("customers")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-green-500" />
          Customer Management
        </h3>
      </div>

      <DataTable
        columns={[
          {
            id: "customer",
            header: "Customer",
            cell: ({ row }) => (
              <span className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                {row.original.company_name || row.original.name || row.original.email || "Customer"}
              </span>
            ),
          },
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ getValue }) => (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                String(getValue() || "").toLowerCase() === "active" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
              }`}>
                {getValue() || "Unknown"}
              </span>
            ),
          },
          {
            id: "modules",
            header: "Modules",
            cell: ({ row }) => (
              <span className={darkMode ? "text-white" : "text-gray-900"}>
                {row.original.modules ?? row.original.enabled_modules?.length ?? "--"}
              </span>
            ),
          },
        ]}
        data={data || []}
        getRowId={(customer, index) => String(customer.id || index)}
        onRowClick={() => onNavigate && onNavigate("customers")}
        emptyMessage="No customers found"
        enableColumnVisibility={false}
      />
    </div>
  );
};

const SystemHealthGauge = ({ widget, data, darkMode, onNavigate }) => {
  const health = data?.analytics?.system_health_score;
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : FireIcon;

  return (
    <div
      className="p-6 text-center"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("virtual-soc")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-orange-500" />
          System Health
        </h3>
      </div>
      <div
        className={`text-4xl font-bold mb-2 ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        {health == null ? "--" : `${health}%`}
      </div>
      <div
        className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}
      >
        {health == null
          ? "No health telemetry available"
          : "Monitored asset health"}
      </div>
    </div>
  );
};

const ComplianceFrameworks = ({ widget, data, darkMode, onNavigate }) => {
  const IconComponent =
    typeof widget?.icon === "function" ? widget.icon : DocumentTextIcon;

  return (
    <div
      className="p-6"
      style={{ cursor: "pointer" }}
      onClick={() => onNavigate && onNavigate("compliance")}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-lg font-semibold flex items-center ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <IconComponent className="w-6 h-6 mr-2 text-blue-500" />
          Compliance Frameworks
        </h3>
      </div>

      <div className="space-y-3">
        {(data || []).length === 0 && (
          <p
            className={
              darkMode ? "text-sm text-gray-400" : "text-sm text-gray-500"
            }
          >
            No framework status data is available.
          </p>
        )}
        {(data || []).map((framework, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg ${
              darkMode ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`font-medium ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {framework.name || framework.framework || "Framework"}
              </span>
              <span
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {framework.progress ??
                  framework.score ??
                  framework.compliance_score ??
                  "--"}
                %
              </span>
            </div>
            <div
              className={`w-full rounded-full h-2 ${
                darkMode ? "bg-gray-600" : "bg-gray-300"
              }`}
            >
              <div
                className={`h-2 rounded-full ${
                  framework.status === "compliant"
                    ? "bg-green-500"
                    : "bg-yellow-500"
                }`}
                style={{
                  width: `${framework.progress ?? framework.score ?? framework.compliance_score ?? 0}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CentralDashboard;
